import { HttpClient, HttpHeaders } from '@angular/common/http';
import {
	inject,
	Injectable,
	InjectionToken,
	TransferState,
} from '@angular/core';

import { EMPTY, firstValueFrom, map, Observable, take } from 'rxjs';

import { HOTEL_SLUG } from '@valk-nx/core/lib/core';
import {
	HALResponse,
	stripHAL,
} from '@valk-nx/core/lib/interfaces/hal.interface';
import {
	ContentHotelInterface,
	HotelOccupancyRestrictionsInterface,
} from '@valk-nx/core/lib/interfaces/hotels.interface';
import { fetchWithTransferState } from '@valk-nx/core/lib/ssr/ssr.helper';
import {
	DealData,
	EventDetailData,
	EventListData,
	HotelDealData,
	MeetingRoomResponseData,
	MiniHotelSiteData,
	PackageData,
	PromotionCodeInterface,
	RoomData,
	SimpleHotelData,
	ThemeEventData,
	ThemePackageData,
} from '@valk-nx/storyblok-types/src/lib/types/content.interface';

export const CONTENT_SERVICE_URL = new InjectionToken<string>(
	'content-service-url',
);
export const CONTENT_WITH_CREDENTIALS = new InjectionToken<string[]>(
	'content-api-cookies',
);

@Injectable({ providedIn: 'root' })
export class ContentService {
	private readonly http = inject(HttpClient);
	private readonly serviceUrl = inject(CONTENT_SERVICE_URL, {
		optional: true,
	});
	private readonly withCredentials = inject<{
		withCredentials: boolean;
	}>(CONTENT_WITH_CREDENTIALS);
	private readonly hotelSlug = inject(HOTEL_SLUG);
	private readonly transferState = inject(TransferState);

	private fetchWithCache<T>(
		path: string,
		key: string,
		language: string,
		extraParams?: string[],
	): Observable<T> {
		const filteredParams = extraParams?.filter(Boolean) ?? [];

		const params = filteredParams.length
			? `&${filteredParams.join('&')}`
			: '';

		const url = `${this.serviceUrl}${path}?language=${language}${params}`;

		return fetchWithTransferState<T>(
			this.transferState,
			this.http,
			key,
			url,
			{
				...this.withCredentials,
			},
		);
	}

	getRooms(language: string) {
		return this.fetchWithCache<HALResponse<RoomData[]>>(
			`hotels/${this.hotelSlug}/rooms`,
			`hotels/${this.hotelSlug}/rooms?language=${language}`,
			language,
		);
	}

	getRoom(roomSlug: string, language: string) {
		return this.fetchWithCache<HALResponse<RoomData>>(
			`hotels/${this.hotelSlug}/rooms/${roomSlug}`,
			`hotels/${this.hotelSlug}/rooms/${roomSlug}?language=${language}`,
			language,
		);
	}

	getPackage(packageSlug: string, language: string) {
		return this.fetchWithCache<HALResponse<PackageData>>(
			`hotels/${this.hotelSlug}/package-batches/${packageSlug}`,
			`hotels/${this.hotelSlug}/package-batches/${packageSlug}?language=${language}`,
			language,
		);
	}

	getPackages(language: string) {
		return this.fetchWithCache<HALResponse<PackageData[]>>(
			`hotels/${this.hotelSlug}/package-batches`,
			`hotels/${this.hotelSlug}/package-batches?language=${language}`,
			language,
			['filtered=true'],
		);
	}

	getMeetingRoom(meetingRoomSlug: string, language: string) {
		return this.fetchWithCache<HALResponse<MeetingRoomResponseData>>(
			`hotels/${this.hotelSlug}/meeting-rooms/${meetingRoomSlug}`,
			`hotels/${this.hotelSlug}/meeting-rooms/${meetingRoomSlug}?language=${language}`,
			language,
		);
	}

	getMeetingRooms(language: string) {
		return this.fetchWithCache<HALResponse<MeetingRoomResponseData[]>>(
			`hotels/${this.hotelSlug}/meeting-rooms`,
			`hotels/${this.hotelSlug}/meeting-rooms?language=${language}`,
			language,
		);
	}

	getEvents(language: string) {
		return this.fetchWithCache<HALResponse<EventListData[]>>(
			`hotels/${this.hotelSlug}/events`,
			`hotels/${this.hotelSlug}/events?language=${language}`,
			language,
		);
	}

	getEvent(
		eventSlug: string,
		hotelSlug: string,
		language: string,
	): Observable<HALResponse<EventDetailData>> {
		return this.fetchWithCache<HALResponse<EventDetailData>>(
			`hotels/${hotelSlug}/events/${eventSlug}`,
			`hotels/${hotelSlug}/events/${eventSlug}?language=${language}`,
			language,
		);
	}

	getHotelInfo(language: string) {
		if (!this.hotelSlug) {
			console.warn(
				'[ContentService] getHotelInfo called without hotelSlug',
			);
			return EMPTY;
		}

		return this.fetchWithCache<HALResponse<ContentHotelInterface>>(
			`hotels/${this.hotelSlug}`,
			`hotels/${this.hotelSlug}?language=${language}`,
			language,
		).pipe(stripHAL);
	}

	getHotelsWithOccupancyRestrictions(language: string) {
		return this.fetchWithCache<
			HALResponse<HotelOccupancyRestrictionsInterface[]>
		>(
			`hotels/occupancy-restrictions`,
			`hotels/occupancy-restrictions?language=${language}`,
			language,
		).pipe(stripHAL);
	}

	getMiniHotelInfo(hotelSlug: string, type: string, language: string) {
		return this.fetchWithCache<HALResponse<MiniHotelSiteData>>(
			`hotels/mini-hotelsite/${hotelSlug}/${type}`,
			`hotels/mini-hotelsite/${hotelSlug}/${type}?language=${language}`,
			language,
		);
	}

	getHotelsByFacilities(
		facilityType: string,
		facilityGuids: string[],
		language: string,
	) {
		const facilityGuidsParams = facilityGuids.length
			? `facilityGuids=${facilityGuids.join(',')}`
			: '';

		return this.fetchWithCache<HALResponse<SimpleHotelData[]>>(
			`${facilityType}-facilities/hotels`,
			`${facilityType}-facilities/hotels?language=${language}${facilityGuidsParams}`,
			language,
			[facilityGuidsParams],
		).pipe(stripHAL);
	}

	getDeal(dealSlug: string, language: string) {
		return this.fetchWithCache<HALResponse<DealData>>(
			`deals/${dealSlug}/hotels`,
			`deals/${dealSlug}/hotels?language=${language}`,
			language,
		);
	}

	getHotelByDeal(dealSlug: string, hotelSlug: string, language: string) {
		return this.fetchWithCache<HALResponse<HotelDealData>>(
			`deals/${dealSlug}/hotels/${hotelSlug}`,
			`deals/${dealSlug}/hotels/${hotelSlug}?language=${language}`,
			language,
		);
	}

	getLegal(keyParam: string, language: string) {
		const hotelSlugParam = this.hotelSlug
			? `hotel-slug=${this.hotelSlug}`
			: '';

		return this.fetchWithCache<HALResponse<string>>(
			`legal/${keyParam}`,
			`legal/${keyParam}?language=${language}${hotelSlugParam}`,
			language,
			[hotelSlugParam],
		);
	}

	getThemePackages(
		themeGuids: string[],
		hotelGuids: string[],
		language: string,
	): Observable<HALResponse<{ packageBatches: ThemePackageData[] }>> {
		const themeGuidsString = themeGuids.length
			? `themeGuids=${themeGuids.join(',')}`
			: '';
		const hotelGuidsString = hotelGuids.length
			? `hotelGuids=${hotelGuids.join(',')}`
			: '';

		return this.fetchWithCache<
			HALResponse<{ packageBatches: ThemePackageData[] }>
		>(
			`package-batches`,
			`package-batches?language=${language}${themeGuidsString}${hotelGuidsString}`,
			language,
			[themeGuidsString, hotelGuidsString],
		);
	}

	getThemeEvents(
		themeGuids: string[],
		hotelGuids: string[],
		language: string,
	): Observable<HALResponse<ThemeEventData[]>> {
		const themeGuidsString = themeGuids.length
			? `themeGuids=${themeGuids.join(',')}`
			: '';
		const hotelGuidsString = hotelGuids.length
			? `hotelGuids=${hotelGuids.join(',')}`
			: '';

		return this.fetchWithCache<HALResponse<ThemeEventData[]>>(
			`events`,
			`events?language=${language}${themeGuidsString}${hotelGuidsString}`,
			language,
			[themeGuidsString, hotelGuidsString],
		);
	}

	submitPromotionCode(
		promotionCode: string,
		language: string,
		hotelSlugs: string[],
		recaptchaToken: string,
		recaptchaVersion: 'v2' | 'v3' = 'v3',
	): Promise<PromotionCodeInterface> {
		const headers = new HttpHeaders({
			'Accept-Language': language,
		});
		const hotelSlugsString = hotelSlugs.length
			? `?hotelSlugs=${hotelSlugs.join(',')}`
			: '';

		return firstValueFrom(
			this.http
				.post<{ data: PromotionCodeInterface }>(
					`${this.serviceUrl}promotions/${promotionCode}/hotels${hotelSlugsString}`,
					{
						language,
						token: recaptchaToken,
						recaptchaVersion,
					},
					{
						headers,
						...this.withCredentials,
					},
				)
				.pipe(
					take(1),
					map((response) => response.data),
				),
		);
	}
}
